/**
 * Installment Card service with API integration
 * 
 * This service provides typed methods for Installment Card CRUD operations
 */

import API_BASE_URL, { HEAVY_MUTATION_TIMEOUT_MS } from '@/config/apiConfig';
import { InstallmentCard, InstallmentCardCreateRequest, InstallmentCardSearchParams, InstallmentCardUpdateRequest, InstallmentCardsListResponse } from '../types/models';
import { apiService } from './api';
import { requireEmployeeId } from './userContext';
import {
  DpCommissionApplicationRequest,
  DpCommissionApplyResponse,
  DpCommissionPreviewResponse,
} from '../features/installment-cards/dp-commission.types';

// Helper to construct full image URL for installment card images
export const getInstallmentCardImageUrl = (imagePath: string): string => {
  if (!imagePath) return '';
  if (imagePath.startsWith('http')) return imagePath;
  const baseUrl = API_BASE_URL;
  return `${baseUrl}${imagePath}`;
};

// Helper to construct installment card front image URL
export const getInstallmentCardFrontImageUrl = (installmentCardId: string): string => {
  const baseUrl = API_BASE_URL;
  return `${baseUrl}/installment-cards/${installmentCardId}/front-image`;
};

// Helper to construct installment card back image URL
export const getInstallmentCardBackImageUrl = (installmentCardId: string): string => {
  const baseUrl = API_BASE_URL;
  return `${baseUrl}/installment-cards/${installmentCardId}/back-image`;
};

// Helper to get authenticated installment card front image URL (returns blob URL)
export const getInstallmentCardFrontImageBlobUrl = async (installmentCardId: string): Promise<string> => {
  try {
    return await apiService.getBlobUrl(
      `/installment-cards/${installmentCardId}/front-image/unscoped`,
    );
  } catch (error) {
    console.error('Failed to load installment card front image:', error);
    return '';
  }
};

// Helper to get authenticated installment card back image URL (returns blob URL)
export const getInstallmentCardBackImageBlobUrl = async (installmentCardId: string): Promise<string> => {
  try {
    return await apiService.getBlobUrl(
      `/installment-cards/${installmentCardId}/back-image/unscoped`,
    );
  } catch (error) {
    console.error('Failed to load installment card back image:', error);
    return '';
  }
};

export const installmentCardService = {
  // List Installment Cards with pagination, search, and sorting
  async listInstallmentCards(params: InstallmentCardSearchParams): Promise<InstallmentCardsListResponse> {
    try {
      const response = await apiService.get<any>('/installment-cards/get-all', {
        page: params.page || 1,
        size: params.size || 20,
        searchTerm: params.searchTerm || '',
        searchFields: params.searchFields || undefined,
        customerId: params.customerId?.trim() || undefined,
        stockId: params.stockId,
        routeId: params.routeId,
        whoAdded: params.whoAdded,
        assignedCollector: params.assignedCollector?.trim() || undefined,
        paymentDateFrom: params.paymentDateFrom,
        paymentDateTo: params.paymentDateTo,
        startDateFrom: params.startDateFrom,
        startDateTo: params.startDateTo,
        status: params.status?.trim() || undefined,
        pendingApproval: params.pendingApproval === true ? true : undefined,
        pendingDpCommission: params.pendingDpCommission === true ? true : undefined,
        approvedOnly: params.approvedOnly === true ? true : undefined,
        isDPcollected:
          params.isDPcollected === true || params.isDPcollected === false
            ? params.isDPcollected
            : undefined,
        location: params.location,
        sortBy: params.sortBy || 'createdAt',
        sortDirection: params.sortDirection ?? -1,
        includeTotals: params.includeTotals === false ? false : undefined,
      });
      
      const responseData = response.data?.data || response.data;
      const cards = responseData?.installmentCards || [];

      return {
        success: true,
        data: {
          installmentCards: cards,
          pagination: responseData?.pagination || {
            page: params.page || 1,
            size: params.size || 20,
            total: 0,
            totalPages: 0,
          },
          ...(responseData?.totals ? { totals: responseData.totals } : {}),
        },
      };
    } catch (error) {
      console.error('[INSTALLMENT-CARD-SERVICE] API request failed:', error);
      throw error;
    }
  },

  async listInstallmentCardsUnscoped(
    params: InstallmentCardSearchParams,
  ): Promise<InstallmentCardsListResponse> {
    const response = await apiService.get<any>('/installment-cards/get-all-unscoped', {
      page: params.page || 1,
      size: params.size || 20,
      searchTerm: params.searchTerm || '',
      searchFields: params.searchFields || undefined,
      customerId: params.customerId?.trim() || undefined,
      stockId: params.stockId,
      routeId: params.routeId,
      whoAdded: params.whoAdded,
      assignedCollector: params.assignedCollector?.trim() || undefined,
      paymentDateFrom: params.paymentDateFrom,
      paymentDateTo: params.paymentDateTo,
      startDateFrom: params.startDateFrom,
      startDateTo: params.startDateTo,
      status: params.status?.trim() || undefined,
      pendingApproval: params.pendingApproval === true ? true : undefined,
      pendingDpCommission: params.pendingDpCommission === true ? true : undefined,
      approvedOnly: params.approvedOnly === true ? true : undefined,
      isDPcollected:
        params.isDPcollected === true || params.isDPcollected === false
          ? params.isDPcollected
          : undefined,
      location: params.location,
      sortBy: params.sortBy || 'createdAt',
      sortDirection: params.sortDirection ?? -1,
      includeTotals: params.includeTotals === false ? false : undefined,
    });

    const responseData = response.data?.data || response.data;
    const cards = responseData?.installmentCards || [];

    return {
      success: true,
      data: {
        installmentCards: cards,
        pagination: responseData?.pagination || {
          page: params.page || 1,
          size: params.size || 20,
          total: 0,
          totalPages: 0,
        },
        ...(responseData?.totals ? { totals: responseData.totals } : {}),
      },
    };
  },

  async getInstallmentCardUnscoped(id: string): Promise<InstallmentCard> {
    const response = await apiService.get<any>(`/installment-cards/${id}/unscoped`);
    const card = response.data?.data || response.data;
    if (!card || !card._id) {
      throw new Error('Invalid response format: missing card data');
    }
    return card as InstallmentCard;
  },

  /**
   * Admin/office: close card (CANCELED) with reason and remark append.
   * Backend: PATCH /installment-cards/:id/close
   */
  async bulkApproveInstallmentCards(
    installmentCardIds: string[],
    assignedCollector?: string,
  ): Promise<{ approved: string[]; failed: { installmentCardId: string; message: string }[] }> {
    const collector = String(assignedCollector ?? '').trim();
    const response = await apiService.post<any>('/installment-cards/bulk-approve', {
      installmentCardIds,
      ...(collector ? { assignedCollector: collector } : {}),
    });
    const data = response?.data?.data ?? response?.data ?? response;
    return {
      approved: Array.isArray(data?.approved) ? data.approved : [],
      failed: Array.isArray(data?.failed) ? data.failed : [],
    };
  },

  async approveInstallmentCard(
    installmentCardId: string,
    body?: {
      remarkAppend?: string;
      location?: string;
      routeId?: string;
      assignedCollector?: string;
    },
  ): Promise<InstallmentCard> {
    const id = String(installmentCardId ?? '').trim();
    if (!id) throw new Error('installmentCardId is required');
    const response = await apiService.patch<any>(
      `/installment-cards/${encodeURIComponent(id)}/approve`,
      body ?? {},
    );
    const card = response?.data?.data ?? response?.data;
    if (!card || !card._id) {
      throw new Error('Invalid response format: missing card data after approve');
    }
    return card;
  },

  async closeInstallmentCard(
    installmentCardId: string,
    body: { closeReason: string; remarkAppend: string },
  ): Promise<InstallmentCard> {
    const id = String(installmentCardId ?? '').trim();
    if (!id) throw new Error('installmentCardId is required');
    const response = await apiService.patch<any>(`/installment-cards/${encodeURIComponent(id)}/close`, body);
    const card = response?.data?.data ?? response?.data;
    if (!card || !card._id) {
      throw new Error('Invalid response format: missing card data after close');
    }
    return card;
  },

  async reactivateInstallmentCard(
    installmentCardId: string,
    body: { remarkAppend?: string } = {},
  ): Promise<InstallmentCard> {
    const id = String(installmentCardId ?? '').trim();
    if (!id) throw new Error('installmentCardId is required');
    const response = await apiService.patch<any>(`/installment-cards/${encodeURIComponent(id)}/reactivate`, body);
    const card = response?.data?.data ?? response?.data;
    if (!card || !card._id) {
      throw new Error('Invalid response format: missing card data after reactivate');
    }
    return card;
  },

  // Get single Installment Card by ID
  async getInstallmentCard(id: string): Promise<InstallmentCard> {
    try {
      console.log('[INSTALLMENT-CARD-SERVICE] Fetching single InstallmentCard from backend API...');
      const response = await apiService.get<any>(`/installment-cards/${id}`);
      
      console.log('[INSTALLMENT-CARD-SERVICE] Single card response:', response);
      
      // Handle nested data structure from backend
      const card = response.data?.data || response.data;
      
      // Check if we have valid card data
      if (!card || !card._id) {
        console.error('[INSTALLMENT-CARD-SERVICE] Invalid card data:', card);
        throw new Error('Invalid response format: missing card data');
      }
      
      return card;
    } catch (error) {
      console.error('[INSTALLMENT-CARD-SERVICE] API request failed:', error);
      throw error;
    }
  },

  async getInstallmentCardsByIds(installmentCardIds: string[]): Promise<InstallmentCard[]> {
    const ids = [...new Set((installmentCardIds || []).map((id) => String(id ?? '').trim()).filter(Boolean))];
    if (!ids.length) return [];

    const response = await apiService.post<any>('/installment-cards/by-ids', {
      installmentCardIds: ids,
    });
    const payload = response?.data?.data ?? response?.data ?? response;
    const cards = payload?.installmentCards ?? [];
    return Array.isArray(cards) ? cards : [];
  },

  /**
   * Admin/office: append one REGULAR installment; non-collected fees are re-split equally (see backend).
   */
  async addExtendedInstallment(installmentCardId: string): Promise<{
    newInstallmentId: string;
    installmentCount: number;
  }> {
    const id = String(installmentCardId ?? '').trim();
    if (!id) throw new Error('installmentCardId is required');
    const response = await apiService.post<any>(`/installment-cards/${encodeURIComponent(id)}/add-extended-installment`, {});
    const data = response?.data?.data ?? response?.data ?? response;
    return {
      newInstallmentId: String(data?.newInstallmentId ?? '').trim(),
      installmentCount: Number(data?.installmentCount) || 0,
    };
  },

  /**
   * For each installmentCardId, returns the first ACTIVE payment whose `referance[]`
   * references a DOWN_PAYMENT installment of that card. Cards with no matching
   * payment are returned with `collectedAmount: 0`.
   */
  async getDownPaymentCollectedForCards(
    installmentCardIds: string[],
  ): Promise<
    Array<{
      installmentCardId: string;
      collectedAmount: number;
      paymentId?: string;
      billNumber?: string;
      paidAt?: string;
    }>
  > {
    const ids = [
      ...new Set(
        (installmentCardIds || [])
          .map((id) => String(id ?? '').trim())
          .filter(Boolean),
      ),
    ];
    if (!ids.length) return [];

    const response = await apiService.post<any>('/installment-cards/down-payment-collected', {
      installmentCardIds: ids,
    });
    const payload = response?.data?.data ?? response?.data ?? response;
    const rows = Array.isArray(payload?.rows) ? payload.rows : [];
    return rows.map((r: any) => ({
      installmentCardId: String(r?.installmentCardId ?? '').trim(),
      collectedAmount: Number(r?.collectedAmount) || 0,
      ...(r?.paymentId ? { paymentId: String(r.paymentId) } : {}),
      ...(r?.billNumber ? { billNumber: String(r.billNumber) } : {}),
      ...(r?.paidAt ? { paidAt: String(r.paidAt) } : {}),
    }));
  },

  // Create new Installment Card
  async createInstallmentCard(data: FormData | InstallmentCardCreateRequest): Promise<InstallmentCard> {
    // Automatically get employeeId from localStorage
    const employeeId = requireEmployeeId();
    try {
      console.log('[INSTALLMENT-CARD-SERVICE] Creating new InstallmentCard via backend API...');
      
      let formData: FormData;
      
      // If data is already FormData (from form submission), use it directly
      if (data instanceof FormData) {
        formData = data;
        // Override whoAdded if it exists
        formData.set('whoAdded', employeeId);
        console.log('[INSTALLMENT-CARD-SERVICE] Using provided FormData');
      } else {
        // Otherwise, create FormData from object
        formData = new FormData();
        formData.append('customerId', data.customerId);
        if (data.pdrId) formData.append('pdrId', data.pdrId);
        if (data.dealValue !== undefined) {
          formData.append('dealValue', data.dealValue.toString());
        }
        if (data.basePayment !== undefined) {
          formData.append('basePayment', data.basePayment.toString());
        }
        if (data.installmentValue !== undefined) {
          formData.append('installmentValue', data.installmentValue.toString());
        }
        if (data.installmentCount !== undefined) {
          formData.append('installmentCount', data.installmentCount.toString());
        }
        formData.append('location', data.location);
        if (data.stockId && data.stockId.length) {
          data.stockId.forEach(id => formData.append('stockId', id));
        }
        if (data.routeId) formData.append('routeId', data.routeId);
        if (data.installmentCardFrontImage) {
          formData.append('installmentCardFrontImage', data.installmentCardFrontImage);
        }
        if (data.installmentCardBackImage) {
          formData.append('installmentCardBackImage', data.installmentCardBackImage);
        }
        formData.append('whoAdded', employeeId);  // Always set whoAdded
        console.log('[INSTALLMENT-CARD-SERVICE] Created FormData from object');
      }
      console.log('[INSTALLMENT-CARD-SERVICE] FormData:', formData);
      const response = await apiService.post<any>('/installment-cards/create', formData, {
        timeout: HEAVY_MUTATION_TIMEOUT_MS,
      });
      
      console.log('[INSTALLMENT-CARD-SERVICE] Create response:', response);
      
      // Handle nested data structure from backend
      const card = response.data?.data || response.data;
      
      return card;
    } catch (error) {
      console.error('[INSTALLMENT-CARD-SERVICE] API request failed:', error);
      throw error;
    }
  },

  async createDraftInstallmentCard(payload: {
    pdrId: string;
    reference: string;
    location?: string;
    routeId?: string;
  }): Promise<InstallmentCard> {
    const response = await apiService.post<any>('/installment-cards/draft', payload, {
      timeout: HEAVY_MUTATION_TIMEOUT_MS,
    });
    return response.data?.data || response.data;
  },

  async completeDraftInstallmentCard(
    installmentCardId: string,
    data: FormData,
  ): Promise<InstallmentCard> {
    const employeeId = requireEmployeeId();
    data.set('whoAdded', employeeId);
    const response = await apiService.patch<any>(
      `/installment-cards/${encodeURIComponent(installmentCardId)}/complete-draft`,
      data,
    );
    return (response as any)?.data?.data || (response as any)?.data || response;
  },

  async discardDraftInstallmentCard(installmentCardId: string): Promise<void> {
    await apiService.post<any>(
      `/installment-cards/${encodeURIComponent(installmentCardId)}/discard-draft`,
      {},
    );
  },

  /**
   * Add Existing Installment Card — custom per-installment amounts (JSON array in FormData as `installments`).
   */
  async createExistingInstallmentCard(data: FormData): Promise<InstallmentCard> {
    const employeeId = requireEmployeeId();
    try {
      const formData = data;
      formData.set('whoAdded', employeeId);
      const response = await apiService.post<any>('/installment-cards/create-existing', formData, {
        timeout: HEAVY_MUTATION_TIMEOUT_MS,
      });
      const card = response.data?.data || response.data;
      return card;
    } catch (error) {
      console.error('[INSTALLMENT-CARD-SERVICE] createExistingInstallmentCard failed:', error);
      throw error;
    }
  },

  // Update existing Installment Card
  async updateInstallmentCard(id: string, data: FormData | InstallmentCardUpdateRequest): Promise<InstallmentCard> {
    // Automatically get employeeId from localStorage
    const employeeId = requireEmployeeId();
    try {
      console.log('[INSTALLMENT-CARD-SERVICE] Updating InstallmentCard via backend API...');
      
      let formData: FormData;
      
      // If data is already FormData (from form submission), use it directly
      if (data instanceof FormData) {
        formData = data;
        // Override whoUpdated if it exists
        formData.set('whoUpdated', employeeId);
        console.log('[INSTALLMENT-CARD-SERVICE] Using provided FormData for update');
      } else {
        // Otherwise, create FormData from object
        formData = new FormData();
        if (data.customerId) formData.append('customerId', data.customerId);
        if (data.pdrId) formData.append('pdrId', data.pdrId);
        if (data.dealValue !== undefined) formData.append('dealValue', data.dealValue.toString());
        if (data.balance !== undefined) formData.append('balance', data.balance.toString());
        if (data.basePayment !== undefined) formData.append('basePayment', data.basePayment.toString());
        if (data.installmentValue !== undefined) formData.append('installmentValue', data.installmentValue.toString());
        if (data.installmentCount !== undefined) formData.append('installmentCount', data.installmentCount.toString());
        if (data.location !== undefined) formData.append('location', data.location);
        if (data.reference !== undefined) formData.append('reference', data.reference);
        if (data.remark !== undefined) formData.append('remark', data.remark);
        if (data.remarkAppend !== undefined) formData.append('remarkAppend', data.remarkAppend);
        if (data.stockId && data.stockId.length) {
          data.stockId.forEach(id => formData.append('stockId', id));
        }
        if (data.routeId) formData.append('routeId', data.routeId);
        if (data.installmentCardFrontImage) {
          formData.append('installmentCardFrontImage', data.installmentCardFrontImage);
        }
        if (data.installmentCardBackImage) {
          formData.append('installmentCardBackImage', data.installmentCardBackImage);
        }
        formData.append('whoUpdated', employeeId);  // Always set whoUpdated
        console.log('[INSTALLMENT-CARD-SERVICE] Created FormData from object for update');
      }
      
      const response = await apiService.patch<any>(`/installment-cards/${id}`, formData);
      
      console.log('[INSTALLMENT-CARD-SERVICE] Update response:', response);
      
      // Handle nested data structure from backend
      const card = response.data?.data || response.data;
      
      return card;
    } catch (error) {
      console.error('[INSTALLMENT-CARD-SERVICE] API request failed:', error);
      throw error;
    }
  },

  async patchInstallmentCardBalance(id: string, balance: number): Promise<InstallmentCard> {
    const rounded = Math.round(Math.max(0, balance));
    return this.updateInstallmentCard(id, { balance: rounded });
  },

  // Process installment payment (update creditValue and balance)
  async processInstallmentPayment(installmentCardId: string, installmentId: string, paymentAmount: number): Promise<void> {
    const employeeId = requireEmployeeId();
    await apiService.post(`/installment-cards/${installmentCardId}/process-payment`, {
      installmentId,
      paymentAmount,
      whoUpdated: employeeId,
    });
  },

  async claimInstallmentPayment(
    installmentCardId: string,
    payload: {
      billNumber?: string;
      amount: number;
      absolutePaymentDate?: string;
      remark?: string;
      routeId?: string;
      applications: { installmentId: string; amount: number }[];
      discountAmount?: number;
    },
  ): Promise<{
    card?: InstallmentCard;
    installments?: unknown[];
    paymentId: string;
    discountPaymentId?: string;
    isPenaltyPayment: boolean;
  }> {
    const employeeId = requireEmployeeId();
    const response = await apiService.post<any>(
      `/installment-cards/${installmentCardId}/claim-payment`,
      {
        ...payload,
        whoUpdated: employeeId,
      },
      { timeout: HEAVY_MUTATION_TIMEOUT_MS },
    );
    return response.data?.data ?? response.data;
  },

  // Delete Installment Card
  async deleteInstallmentCard(id: string): Promise<void> {
    try {
      console.log('[INSTALLMENT-CARD-SERVICE] Deleting InstallmentCard via backend API...');
      await apiService.delete(`/installment-cards/${id}`);
      console.log('[INSTALLMENT-CARD-SERVICE] Delete successful');
    } catch (error) {
      console.error('[INSTALLMENT-CARD-SERVICE] API request failed:', error);
      throw error;
    }
  },

  async bulkDeleteInstallmentCards(
    password: string,
    installmentCardIds: string[],
  ): Promise<{ deletedCardIds: string[]; paymentsMarkedDeleted: number }> {
    const body = await apiService.post<{
      success: boolean;
      message: string;
      data: { deletedCardIds: string[]; paymentsMarkedDeleted: number };
    }>('/installment-cards/bulk-delete', { password, installmentCardIds });
    // ApiResponseInterceptor wraps controller body as { status, data }; controller returns { success, message, data: payload }.
    const outer = body as unknown as {
      data?: { data?: { deletedCardIds?: string[]; paymentsMarkedDeleted?: number; paymentsArchived?: number } };
    };
    const payload =
      outer?.data?.data ??
      (outer?.data as { deletedCardIds?: string[]; paymentsMarkedDeleted?: number; paymentsArchived?: number } | undefined);
    const paymentsMarkedDeleted =
      typeof payload?.paymentsMarkedDeleted === 'number'
        ? payload.paymentsMarkedDeleted
        : typeof payload?.paymentsArchived === 'number'
          ? payload.paymentsArchived
          : 0;
    return {
      deletedCardIds: Array.isArray(payload?.deletedCardIds) ? payload.deletedCardIds : [],
      paymentsMarkedDeleted,
    };
  },

  async bulkAdminUpdateInstallmentCards(
    installmentCardIds: string[],
    patch: {
      approvedBy?: string | null;
      assignedCollector?: string | null;
      isDBCpaid?: boolean | null;
      isDPcollected?: boolean | null;
      routeId?: string | null;
      status?: 'ACTIVE' | 'ARCHIVED' | 'DELETED' | 'PENDING' | 'CANCELED';
    },
  ): Promise<{
    updated: string[];
    failed: { installmentCardId: string; message: string }[];
  }> {
    const body = await apiService.post<{
      success: boolean;
      message: string;
      data: { updated: string[]; failed: { installmentCardId: string; message: string }[] };
    }>('/installment-cards/bulk-admin-update', {
      installmentCardIds,
      ...patch,
    });
    const outer = body as unknown as {
      data?: {
        data?: { updated?: string[]; failed?: { installmentCardId: string; message: string }[] };
        updated?: string[];
        failed?: { installmentCardId: string; message: string }[];
      };
    };
    const payload = outer?.data?.data ?? outer?.data;
    return {
      updated: Array.isArray(payload?.updated) ? payload.updated : [],
      failed: Array.isArray(payload?.failed) ? payload.failed : [],
    };
  },

  async discardInstallmentCards(
    installmentCardIds: string[],
    reason?: string,
  ): Promise<{ results: DiscardInstallmentCardResult[]; cancelled: number }> {
    const body = await apiService.post<{
      success: boolean;
      message: string;
      data: { results: DiscardInstallmentCardResult[]; cancelled: number };
    }>('/installment-cards/bulk-discard', {
      installmentCardIds,
      ...(reason ? { reason } : {}),
    });
    // Same double-wrap handling as bulkDelete (ApiResponseInterceptor + controller envelope).
    const outer = body as unknown as {
      data?: { data?: { results?: DiscardInstallmentCardResult[]; cancelled?: number } };
    };
    const payload = outer?.data?.data
      ?? (outer?.data as { results?: DiscardInstallmentCardResult[]; cancelled?: number } | undefined);
    return {
      results: Array.isArray(payload?.results) ? payload.results : [],
      cancelled: typeof payload?.cancelled === 'number' ? payload.cancelled : 0,
    };
  },

  async previewDpCommission(
    dto: DpCommissionApplicationRequest,
  ): Promise<DpCommissionPreviewResponse> {
    const body = await apiService.post<{
      success: boolean;
      data: DpCommissionPreviewResponse;
    }>('/installment-cards/dp-commission/preview', dto);
    const outer = body as unknown as { data?: DpCommissionPreviewResponse | { data?: DpCommissionPreviewResponse } };
    const payload = outer?.data && 'ok' in outer.data ? outer.data : (outer?.data as { data?: DpCommissionPreviewResponse })?.data;
    if (!payload || typeof payload.ok !== 'boolean') {
      throw new Error('Invalid DP commission preview response');
    }
    return payload;
  },

  async applyDpCommission(dto: DpCommissionApplicationRequest): Promise<DpCommissionApplyResponse> {
    const body = await apiService.post<{
      success: boolean;
      data: DpCommissionApplyResponse;
    }>('/installment-cards/dp-commission/apply', dto);
    const outer = body as unknown as { data?: DpCommissionApplyResponse | { data?: DpCommissionApplyResponse } };
    const payload = outer?.data && 'remunerationId' in outer.data
      ? outer.data
      : (outer?.data as { data?: DpCommissionApplyResponse })?.data;
    if (!payload?.remunerationId) {
      throw new Error('Invalid DP commission apply response');
    }
    return payload;
  },
};

export interface DiscardInstallmentCardResult {
  installmentCardId: string;
  ok: boolean;
  error?: string;
  warnings?: string[];
}

